import { Head, Link, usePage } from "@inertiajs/react";
import { History, CreditCard } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { RunningCampaignHero, LockedCampaignHero } from "@/components/schools/DashboardHeros";
import SetupSummaryCard from "@/components/schools/setup/SetupSummaryCard";
import ShareDonationPageCard from "@/components/schools/ShareDonationPageCard";
import { TopStudentsCard, TopClassroomsCard } from "@/components/schools/CampaignLeaderboards";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

/**
 * The school admin dashboard. Anchored on the "current campaign" — the
 * one running, upcoming, or most recently locked. Schools think about
 * "our spring read-a-thon" not "campaign #17", so the dashboard always
 * shows the relevant one and surfaces history via a secondary link.
 *
 * Three states:
 *   1. no campaign, or a draft still in setup → SetupSummaryCard (progress + next step,
 *      leading into the setup guide)
 *   2. setup complete with donations open, or active|grace → RunningCampaignHero (live metrics)
 *   3. locked                  → LockedCampaignHero (final results + start next)
 *
 * The heroes live in components/schools so the help centre can render
 * the same card in a "screenshot" frame without duplicating UI code.
 */
export default function Dashboard({
  school,
  membership,
  square_connection,
  current_campaign,
  setup,
  new_campaign_path,
  campaigns_path,
  campaigns_count,
}) {
  const { auth } = usePage().props;
  const heroState = setup ? "setup" : current_campaign.state === "locked" ? "locked" : "running";
  const firstName = auth?.user?.name?.split(" ")[0];

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={heroLabel(heroState, current_campaign)}
      title={current_campaign?.name || (firstName ? `Welcome, ${firstName}` : "Welcome to PagePledge")}
      hideSetupBanner={heroState === "setup" || current_campaign?.state === "draft"}
      actions={
        membership.school_admin && campaigns_count > 1 && (
          <Button variant="outline" size="sm" asChild>
            <Link href={campaigns_path}>
              <History className="h-4 w-4" />
              Past campaigns
            </Link>
          </Button>
        )
      }
    >
      <Head title={`${school.name} · Dashboard`} />

      {heroState === "setup" && <SetupSummaryCard setup={setup} />}

      {heroState === "running" && (
        <div className="space-y-6">
          {!square_connection.connected && <SquareDisconnectedAlert connection={square_connection} canManage={membership.school_admin} />}
          <RunningCampaignHero campaign={current_campaign} membership={membership} />
        </div>
      )}

      {heroState === "locked" && (
        <LockedCampaignHero campaign={current_campaign} membership={membership} new_campaign_path={new_campaign_path} />
      )}

      {current_campaign && heroState !== "setup" &&
        ((current_campaign.top_students?.by_money?.length > 0) || current_campaign.top_classrooms?.by_money?.length > 0) && (
        <div className="grid gap-6 lg:grid-cols-2 mt-8">
          <TopStudentsCard students={current_campaign.top_students} />
          <TopClassroomsCard classrooms={current_campaign.top_classrooms} />
        </div>
      )}

      {(heroState === "running" || current_campaign?.display_state === "early_donations") && (
        <div className="mt-8">
          <ShareDonationPageCard
            url={school.public_url}
            tvDisplayUrl={school.tv_display_url}
            schoolName={school.name}
          />
        </div>
      )}
    </SchoolLayout>
  );
}

// Square was disconnected mid-campaign: donations fail until it's back.
function SquareDisconnectedAlert({ connection, canManage }) {
  return (
    <Alert variant="destructive">
      <CreditCard className="h-4 w-4" />
      <AlertTitle>Square isn't connected</AlertTitle>
      <AlertDescription className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center">
        <span className="flex-1 text-foreground">Donations can't go through until your school's Square account is connected again.</span>
        {canManage && (
          <Button size="sm" asChild>
            <Link href={connection.connect_path}>Reconnect Square</Link>
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

function heroLabel(state, campaign) {
  switch (state) {
    case "setup":   return !campaign ? "Welcome" : campaign.display_state === "early_donations" ? "Donations open" : "Getting ready";
    case "running": return campaign.display_state === "early_donations" ? "Donations open" : campaign.state === "grace" ? "Wrapping up" : "Running now";
    case "locked":  return "Final results";
  }
}
